// The redeem-then-set-password flow shared by the two admin screens that arrive
// holding a one-time credential: /reset-password and /accept-invite.
//
// Why those pages redeem into a throwaway, memory-only client (see
// createEphemeralAdminSupabase) rather than the admin client:
//
// A recovery or invite link is a one-time credential that, once redeemed, IS a
// live admin session. Redeeming it straight into the admin client wrote that
// session to localStorage before any password was chosen, so anything that
// opened the link — a person, or a mail scanner's JS sandbox — walked away
// signed in (and, for an invite, the real invitee found their link spent). The
// pages now (a) wait for a click before redeeming at all, and (b) hold the
// redeemed session in memory until updateUser() has succeeded. Only then is it
// handed to the persisted admin client. A tab that leaves without finishing
// revokes the in-memory session (abandonSession).
//
// OneTimeLinkSession holds the page's per-attempt state (the latch, the
// in-memory client, "has the page been left", "is it done") so both pages get
// the same rules and the rules are testable. Structural types, like
// redeemCredentials.ts, so all of this runs against plain objects — apps/admin
// has no jsdom.

import {
  redeemCredentials,
  type AuthLike,
  type Credentials,
  type RedeemKind,
} from './redeemCredentials.ts';

type AuthError = unknown;
type SessionTokens = { access_token?: string | null; refresh_token?: string | null } | null;

/** The throwaway client the link was redeemed into. */
export type LinkSessionAuthLike = {
  updateUser(attrs: { password: string }): Promise<{ error?: AuthError }>;
  getSession(): Promise<{ data: { session: SessionTokens }; error?: AuthError }>;
  signOut(opts: { scope: 'local' }): Promise<{ error?: AuthError }>;
};

/** The persisted admin client the session is handed to once the password is set. */
export type PersistAuthLike = {
  setSession(args: {
    access_token: string;
    refresh_token: string;
  }): Promise<{ data?: { session?: SessionTokens } | null; error?: AuthError }>;
};

/**
 * The password WAS set, but the session could not be moved onto the admin
 * client. The page must not offer the form again (a retry would re-submit the
 * same password, which GoTrue refuses as unchanged) — it sends the user to sign
 * in with the password they just chose.
 */
export class PasswordSetSignInFailed extends Error {
  constructor() {
    super('Your password was set. Sign in with your new password.');
  }
}

/**
 * Set the new password on the in-memory session, then persist that session on
 * the admin client. Resolves with the admin access token, or throws: the
 * updateUser error as-is (the form stays open), or PasswordSetSignInFailed.
 */
export async function completePasswordSet(
  temp: LinkSessionAuthLike,
  persist: PersistAuthLike,
  password: string,
): Promise<string> {
  const { error: updateErr } = await temp.updateUser({ password });
  if (updateErr) throw updateErr;

  // From here the password is set, so every failure is the same one.
  try {
    const { data } = await temp.getSession();
    const access = data.session?.access_token;
    const refresh = data.session?.refresh_token;
    if (!access || !refresh) throw new PasswordSetSignInFailed();
    const out = await persist.setSession({ access_token: access, refresh_token: refresh });
    if (out.error) throw new PasswordSetSignInFailed();
    return out.data?.session?.access_token ?? access;
  } catch {
    throw new PasswordSetSignInFailed();
  }
}

/**
 * Revoke a redeemed-but-unfinished session. `scope: 'local'` ends THIS session
 * only — the supabase-js default is global, which would also sign the admin out
 * on every other device. Best-effort: a tab that is closing may not get the
 * request out, and a failure here must never surface to the user.
 */
export async function abandonSession(temp: LinkSessionAuthLike): Promise<void> {
  try {
    await temp.signOut({ scope: 'local' });
  } catch {
    // nothing to do — the session simply expires on its own
  }
}

/** What `redeem()` reports when it did not throw. */
export type RedeemOutcome =
  /** Redeemed; the password form may open. */
  | { status: 'redeemed'; email: string | null }
  /** A redeem is already in flight or done — a double tap. Nothing happened. */
  | { status: 'ignored' }
  /** The page was left while the redeem was in flight; the late session was revoked. */
  | { status: 'left' };

/**
 * One visit's worth of state for a click-gated one-time link. The page keeps
 * one instance in state, calls `enter()`/`leave()` from its mount effect (and
 * `leave()` from pagehide), `redeem()` from its Continue button, and
 * `complete()` from its password form.
 */
export class OneTimeLinkSession<C extends { auth: AuthLike & LinkSessionAuthLike }> {
  private client: C | null = null;
  // Latch for Continue. A double tap lands before React re-renders the button
  // away, and two redeems of a one-time link leave the FIRST client holding a
  // live session that nothing references (the second overwrote it and failed
  // "already used"), so leave() could never revoke it.
  private redeeming = false;
  private left = false;
  private done = false;

  constructor(
    private readonly creds: Credentials | null,
    private readonly kind: RedeemKind,
    private readonly makeClient: () => C,
  ) {}

  /** The in-memory client holding the redeemed session, once there is one. */
  get held(): C | null {
    return this.client;
  }

  /**
   * Redeem the credential into a fresh memory-only client. Throws the redeem's
   * error (and re-arms, since a failed redeem holds no session).
   */
  async redeem(): Promise<RedeemOutcome> {
    if (this.redeeming || this.client) return { status: 'ignored' };
    this.redeeming = true;
    try {
      const temp = this.makeClient();
      const { email } = await redeemCredentials(temp.auth, this.creds, this.kind);
      // Published only once it holds a session, so leave() always reaches the
      // one client that does. Left mid-redeem: revoke the late session now.
      this.client = temp;
      if (this.left) {
        void abandonSession(temp.auth);
        return { status: 'left' };
      }
      return { status: 'redeemed', email };
    } catch (err) {
      this.redeeming = false;
      throw err;
    }
  }

  /**
   * The page is (re)mounted. Clears `left`, because React StrictMode runs a
   * dev-only unmount/remount on first render: without this, that simulated
   * unmount's leave() would mark the page left for good, and every redeem
   * afterwards would be revoked on arrival with the page stuck on its spinner.
   * A real unmount is never followed by enter() on the same instance.
   */
  enter(): void {
    this.left = false;
  }

  /** The page is going away. Revokes a redeemed session unless it was completed. */
  leave(): void {
    this.left = true;
    if (this.client && !this.done) {
      void abandonSession(this.client.auth);
      // Forget the revoked client: a page restored from the back/forward cache
      // runs no effect, and a still-set `held` would send the form into
      // updateUser on a dead session ("Auth session missing") instead of the
      // page's own "no longer active" message.
      this.client = null;
    }
  }

  /**
   * Set the password and persist the session (completePasswordSet). Throws
   * when nothing is held — callers check `held` first to word that error.
   */
  async complete(persist: PersistAuthLike, password: string): Promise<string> {
    const temp = this.client;
    if (!temp) throw new Error('This link is no longer active.');
    const token = await completePasswordSet(temp.auth, persist, password);
    this.done = true;
    return token;
  }
}
